import { Helmet } from "react-helmet-async";
import { CANONICAL_ORIGIN } from "@/lib/seo/routePolicy";
import { serializeJsonLd } from "@/lib/seo/jsonLd";
import { SITE_METADATA } from "@/lib/seo/siteMetadata";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "article" | "website";
  twitterHandle?: string;
  noindex?: boolean;
  schema?: object;
}

const siteUrl = CANONICAL_ORIGIN;

// Canonical/og:url must always live on the site origin — rewrite any host.
const toCanonicalUrl = (value: string) => {
  const normalizedUrl = new URL(value, siteUrl);

  return `${siteUrl}${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`;
};

// Images may be hosted elsewhere (e.g. Supabase storage): resolve relative
// paths against the site origin but preserve an already-absolute URL's host.
const toAbsoluteImageUrl = (value: string) => new URL(value, siteUrl).href;

const defaults = {
  ...SITE_METADATA,
  type: "website" as const,
};

export default function SEO({
  title = defaults.title,
  description = defaults.description,
  image = defaults.image,
  url = defaults.url,
  type = defaults.type,
  twitterHandle = defaults.twitterHandle,
  noindex = false,
  schema,
}: SEOProps) {
  const fullImageUrl = toAbsoluteImageUrl(image);
  const fullUrl = toCanonicalUrl(url);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <link rel="canonical" href={fullUrl} />
      )}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={defaults.title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />

      {schema && (
        <script type="application/ld+json">{serializeJsonLd(schema)}</script>
      )}
    </Helmet>
  );
}
